<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useMessageTranslation } from 'dashboard/composables/useMessageTranslation';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { setI18nLocale } from 'dashboard/i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import UserPhoneCredentials from './UserPhoneCredentials.vue';
import MessageSignature from './MessageSignature.vue';
import MessageTranslation from './MessageTranslation.vue';
import FontSize from './FontSize.vue';
import UILanguage from './UILanguage.vue';
import HotKeyCard from './HotKeyCard.vue';
import ChangePassword from './ChangePassword.vue';
import NotificationPreferences from './NotificationPreferences.vue';
import AudioNotifications from './AudioNotifications.vue';
import FormSection from 'dashboard/components/FormSection.vue';
import AccessToken from './AccessToken.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import Policy from 'dashboard/components/policy.vue';
import FolderOrder from './FolderOrder.vue';
import SidebarConfiguration from './SidebarConfiguration.vue';
import AutoNavigationSetting from './AutoNavigationSetting.vue';
import EmailPreviewFormat from './EmailPreviewFormat.vue';
import PasteSettings from './PasteSettings.vue';
import ReplyBoxHeight from './ReplyBoxHeight.vue';
import {
  ROLES,
  CONVERSATION_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export default {
  components: {
    MessageSignature,
    MessageTranslation,
    FormSection,
    FontSize,
    UILanguage,
    UserProfilePicture,
    Policy,
    UserBasicDetails,
    UserPhoneCredentials,
    HotKeyCard,
    ChangePassword,
    NotificationPreferences,
    AudioNotifications,
    AccessToken,
    ConfirmationModal,
    FolderOrder,
    SidebarConfiguration,
    AutoNavigationSetting,
    EmailPreviewFormat,
    PasteSettings,
    ReplyBoxHeight,
  },
  mixins: [globalConfigMixin],
  setup() {
    const { isEditorHotKeyEnabled, updateUISettings } = useUISettings();
    const { currentFontSize, updateFontSize } = useFontSize();
    const { currentTranslationMode, updateTranslationMode } =
      useMessageTranslation();

    return {
      currentFontSize,
      updateFontSize,
      currentTranslationMode,
      updateTranslationMode,
      isEditorHotKeyEnabled,
      updateUISettings,
    };
  },
  data() {
    return {
      avatarFile: '',
      avatarUrl: '',
      emailSignaturePictureFile: '',
      emailSignaturePictureUrl: '',
      name: '',
      displayName: '',
      position: '',
      customLine: '',
      email: '',
      messageSignature: '',
      hotKeys: [
        {
          key: 'enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-enter.svg',
          darkImage: '/assets/images/dashboard/profile/hot-key-enter-dark.svg',
        },
        {
          key: 'cmd_enter',
          title: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.HEADING'
          ),
          description: this.$t(
            'PROFILE_SETTINGS.FORM.SEND_MESSAGE.CARD.CMD_ENTER_KEY.CONTENT'
          ),
          lightImage: '/assets/images/dashboard/profile/hot-key-ctrl-enter.svg',
          darkImage:
            '/assets/images/dashboard/profile/hot-key-ctrl-enter-dark.svg',
        },
      ],
      notificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      audioNotificationPermissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      currentAccountId: 'getCurrentAccountId',
      globalConfig: 'globalConfig/get',
      phoneAvailable: 'phone/available',
      chromeExtensionAvailable: 'phone/getChromeExtensionAvailable',
      conversationCustomViews: 'customViews/getConversationCustomViews',
      uiSettings: 'getUISettings',
    }),
    autoNavigateToNextConversation() {
      return this.uiSettings.auto_navigate_to_next_conversation || false;
    },
    emailPreviewFormat() {
      return this.uiSettings.email_preview_format || 'subject';
    },
    disableShortcuts() {
      return this.uiSettings.disable_shortcuts || false;
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('customViews/get', 'conversation');
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.emailSignaturePictureUrl =
        this.currentUser.email_signature_picture_url;
      this.displayName = this.currentUser.display_name;
      this.position = this.currentUser.position;
      this.customLine = this.currentUser.custom_line;
      this.messageSignature = this.currentUser.message_signature;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName, position, customLine } = userAttributes;
      const hasEmailChanged = this.currentUser.email !== email;
      this.name = name !== undefined ? name : this.name;
      this.email = email !== undefined ? email : this.email;
      this.displayName =
        displayName !== undefined ? displayName : this.displayName;
      this.position = position !== undefined ? position : this.position;
      this.customLine = customLine !== undefined ? customLine : this.customLine;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
        position: this.position,
        customLine: this.customLine,
        avatar: this.avatarFile,
        emailSignaturePicture: this.emailSignaturePictureFile,
        account_id: this.currentAccountId,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) {
        setTimeout(() => {
          clearCookiesOnLogout();
        }, 5500);
      }
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    updateProfilePicture({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        this.avatarFile = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
    updateEmailSignaturePicture({ file, url }) {
      this.emailSignaturePictureFile = file;
      this.emailSignaturePictureUrl = url;
    },
    async deleteEmailSignaturePicture() {
      try {
        await this.$store.dispatch('deleteEmailSignaturePicture');
        this.emailSignaturePictureUrl = '';
        this.emailSignaturePictureFile = '';
        useAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_PICTURE.DELETE_SUCCESS'
          )
        );
      } catch (error) {
        useAlert(
          this.$t('PROFILE_SETTINGS.FORM.EMAIL_SIGNATURE_PICTURE.DELETE_FAILED')
        );
      }
    },
    toggleHotKey(key) {
      this.hotKeys = this.hotKeys.map(hotKey =>
        hotKey.key === key ? { ...hotKey, active: !hotKey.active } : hotKey
      );
      this.updateUISettings({ editor_message_key: key });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.UPDATE_SUCCESS'));
    },
    async onCopyToken(value) {
      await copyTextToClipboard(value);
      useAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    async onRegenerateToken() {
      const ok = await this.$refs.confirmRegenerateToken.showConfirmation();
      if (!ok) return;

      try {
        await this.$store.dispatch('regenerateAccessToken');
        useAlert(
          this.$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.REGENERATE_SUCCESS')
        );
      } catch (error) {
        useAlert(
          parseAPIErrorResponse(error) ||
            this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
        );
      }
    },
    async updateUILanguage(language) {
      this.updateUISettings({ ui_locale: language || null });
      const effectiveLocale =
        language ||
        this.$store.getters['accounts/getAccount'](this.currentAccountId)
          ?.locale ||
        'en';
      setI18nLocale(effectiveLocale);
      useAlert(
        this.$t(
          'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.UI_LANGUAGE.API_SUCCESS'
        )
      );
    },
    updateAutoNavigationSetting(enabled) {
      this.updateUISettings({ auto_navigate_to_next_conversation: enabled });
      useAlert(this.$t('PROFILE_SETTINGS.FORM.AUTO_NAVIGATION.UPDATE_SUCCESS'));
    },
    updateEmailPreviewFormat(format) {
      this.updateUISettings({ email_preview_format: format });
      useAlert(
        this.$t('PROFILE_SETTINGS.FORM.EMAIL_PREVIEW_FORMAT.UPDATE_SUCCESS')
      );
    },
    updateDisableShortcuts(enabled) {
      this.updateUISettings({ disable_shortcuts: enabled });
      useAlert(
        this.$t('PROFILE_SETTINGS.FORM.DISABLE_SHORTCUTS.UPDATE_SUCCESS')
      );
    },
  },
};
</script>

<template>
  <div class="grid py-16 px-5 font-inter mx-auto gap-16 sm:max-w-screen-md">
    <div class="flex flex-col gap-6">
      <h2 class="text-2xl font-medium text-ash-900">
        {{ $t('PROFILE_SETTINGS.TITLE') }}
      </h2>
      <UserProfilePicture
        :src="avatarUrl"
        :name="name"
        @change="updateProfilePicture"
        @delete="deleteProfilePicture"
      />
      <UserBasicDetails
        :name="name"
        :display-name="displayName"
        :position="position"
        :custom-line="customLine"
        :email="email"
        :email-signature-picture-url="emailSignaturePictureUrl"
        :email-enabled="!globalConfig.disableUserProfileUpdate"
        @update-user="updateProfile"
        @update-email-signature-picture="updateEmailSignaturePicture"
        @delete-email-signature-picture="deleteEmailSignaturePicture"
      />
    </div>
    <FormSection
      :title="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.NOTE')"
    >
      <FontSize
        :value="currentFontSize"
        :label="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.TITLE')"
        :description="
          $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.FONT_SIZE.NOTE')
        "
        @change="updateFontSize"
      />
      <UILanguage
        :value="uiSettings.ui_locale || ''"
        :label="$t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.UI_LANGUAGE.TITLE')"
        :description="
          $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.UI_LANGUAGE.NOTE')
        "
        @change="updateUILanguage"
      />
      <MessageTranslation
        :value="currentTranslationMode"
        :label="
          $t(
            'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.MESSAGE_TRANSLATION.TITLE'
          )
        "
        :description="
          $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.MESSAGE_TRANSLATION.NOTE')
        "
        @change="updateTranslationMode"
      />
      <PasteSettings />
      <AutoNavigationSetting
        :value="autoNavigateToNextConversation"
        :label="$t('PROFILE_SETTINGS.FORM.AUTO_NAVIGATION.TITLE')"
        :description="$t('PROFILE_SETTINGS.FORM.AUTO_NAVIGATION.NOTE')"
        @change="updateAutoNavigationSetting"
      />
      <EmailPreviewFormat
        :value="emailPreviewFormat"
        :label="$t('PROFILE_SETTINGS.FORM.EMAIL_PREVIEW_FORMAT.TITLE')"
        :description="$t('PROFILE_SETTINGS.FORM.EMAIL_PREVIEW_FORMAT.NOTE')"
        @change="updateEmailPreviewFormat"
      />
      <ReplyBoxHeight />
    </FormSection>
    <FormSection
      v-if="conversationCustomViews && conversationCustomViews.length > 0"
      :title="$t('PROFILE_SETTINGS.FORM.FOLDER_ORDER.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.FOLDER_ORDER.NOTE')"
    >
      <FolderOrder />
    </FormSection>
    <FormSection
      :title="$t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.NOTE')"
    >
      <SidebarConfiguration />
    </FormSection>
    <FormSection
      v-if="phoneAvailable"
      :title="$t('PROFILE_SETTINGS.FORM.PHONE_SETTINGS.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.PHONE_SETTINGS.NOTE')"
    >
      <UserPhoneCredentials />
    </FormSection>

    <FormSection
      :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
      :description="
        $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.DESCRIPTION')
      "
    >
      <div>
        {{ $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE') }}
      </div>
      <MessageSignature
        :message-signature="messageSignature"
        @update-signature="updateSignature"
      />
    </FormSection>
    <FormSection
      :title="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.SEND_MESSAGE.NOTE')"
    >
      <div
        class="flex flex-col justify-between w-full gap-5 sm:gap-4 sm:flex-row"
      >
        <button
          v-for="hotKey in hotKeys"
          :key="hotKey.key"
          class="px-0 reset-base w-full sm:flex-1 rounded-xl outline-1 outline"
          :class="
            isEditorHotKeyEnabled(hotKey.key)
              ? 'outline-n-brand/30'
              : 'outline-n-weak'
          "
        >
          <HotKeyCard
            :key="hotKey.title"
            :title="hotKey.title"
            :description="hotKey.description"
            :light-image="hotKey.lightImage"
            :dark-image="hotKey.darkImage"
            :active="isEditorHotKeyEnabled(hotKey.key)"
            @click="toggleHotKey(hotKey.key)"
          />
        </button>
      </div>
      <AutoNavigationSetting
        :value="disableShortcuts"
        :label="$t('PROFILE_SETTINGS.FORM.DISABLE_SHORTCUTS.TITLE')"
        :description="$t('PROFILE_SETTINGS.FORM.DISABLE_SHORTCUTS.NOTE')"
        @change="updateDisableShortcuts"
      />
    </FormSection>
    <FormSection
      v-if="!globalConfig.disableUserProfileUpdate"
      :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
    >
      <ChangePassword />
    </FormSection>
    <Policy :permissions="audioNotificationPermissions">
      <FormSection
        :title="$t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')"
        :description="
          $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
        "
      >
        <AudioNotifications />
      </FormSection>
    </Policy>
    <Policy :permissions="notificationPermissions">
      <FormSection :title="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TITLE')">
        <NotificationPreferences />
      </FormSection>
    </Policy>
    <FormSection
      :title="$t('PROFILE_SETTINGS.FORM.CHROME_EXTENSION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.CHROME_EXTENSION.NOTE')"
    >
      <div class="flex items-center gap-3">
        <div
          v-if="chromeExtensionAvailable"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-800 bg-green-50 border border-green-200 rounded-lg dark:text-green-100 dark:bg-green-900 dark:border-green-800"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
            />
          </svg>
          {{ $t('PROFILE_SETTINGS.FORM.CHROME_EXTENSION.ALREADY_INSTALLED') }}
        </div>
        <a
          v-else
          href="https://chromewebstore.google.com/detail/jarabot-contact-center-no/oklcddfdeoafjgnnmibbpnccegfaohld?authuser=2&hl=cs"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-ash-900 bg-ash-50 border border-ash-200 rounded-lg hover:bg-ash-100 focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-offset-2 transition-colors dark:text-ash-100 dark:bg-ash-800 dark:border-ash-700 dark:hover:bg-ash-700"
        >
          {{ $t('PROFILE_SETTINGS.FORM.CHROME_EXTENSION.INSTALL_BUTTON') }}
        </a>
      </div>
    </FormSection>
    <FormSection
      v-if="currentUser.api_access_allowed"
      :title="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
      :description="
        useInstallationName(
          $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.NOTE'),
          globalConfig.installationName
        )
      "
    >
      <AccessToken
        :value="currentUser.access_token"
        @on-copy="onCopyToken"
        @on-regenerate="onRegenerateToken"
      />
      <ConfirmationModal
        ref="confirmRegenerateToken"
        :title="
          $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.REGENERATE_CONFIRM_TITLE')
        "
        :description="
          $t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.REGENERATE_CONFIRM_MESSAGE')
        "
        :confirm-label="$t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.REGENERATE')"
        cancel-label="Cancel"
      />
    </FormSection>
  </div>
</template>
